import { cssProps } from '@oracle/oraclejet-cssprops';
import { ComponentChildren, Ref } from 'preact';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { Flex } from '../UNSAFE_Flex';
import { classNames } from '../utils/UNSAFE_classNames';
import { loadingStyles } from './TextFieldUtils';

type VariantProps = {
  contentVariant?: 'input' | 'textarea';
  statusVariant?: 'error' | 'none' | 'warning';
  styleVariant?: 'default' | 'embedded';
};

type Props = VariantProps & {
  mainContent?: ComponentChildren;
  insideLabel?: ComponentChildren;
  startContent?: ComponentChildren;
  endContent?: ComponentChildren;
  resize?: 'horizontal' | 'vertical' | 'both';
  rootRef?: Ref<HTMLDivElement>;
};

// TODO: replace this with var(--oj-button-height) once it is available
const ojButtonHeight = '2.75rem';
// TODO: replace this with var(--oj-button-sm-height) once it is available
const ojButtonSmHeight = '2.25rem';
// TODO: replace this with var(--oj-button-icon-size) once it is available
const ojButtonIconSize = 'var(--oj-c-PRIVATE-DO-NOT-USE-core-icon-size-lg)';
// TODO: replace this with var(--oj-button-sm-icon-size) once it is available
const ojButtonSmIconSize = '1.25rem';

const textFieldContainerStyles = {
  base: cssProps`${{
    position: 'relative',
    boxSizing: 'border-box',
    // TODO: Use Flex component instead of CSS
    display: 'flex',
    alignItems: 'center',
    overflowX: 'hidden',
    overflowY: 'hidden',
    transitionProperty: 'border-color',
    transitionDuration: '.1s',
    transitionTimingFunction: 'linear'
  }}`,
  defaultStyles: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-bg-color)',
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-border-color)',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-border-radius)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height)'
  }}`,
  embeddedStyles: cssProps`${{
    backgroundColor: 'inherit',
    height: '100%',
    width: '100%'
  }}`,
  showBorder: cssProps`${{
    borderStyle: 'solid',
    borderWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-border-width)'
  }}`,
  textarea: cssProps`${{
    height: 'auto'
  }}`,
  labelInside: cssProps`${{
    minHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-height)'
  }}`,
  focused: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-border-color-focus)',
    boxShadow: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-box-shadow-focus)'
  }}`,
  resize: {
    both: cssProps`${{
      resize: 'both'
    }}`,
    horizontal: cssProps`${{
      resize: 'horizontal'
    }}`,
    vertical: cssProps`${{
      resize: 'vertical'
    }}`
  },
  disabled: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-bg-color-disabled)',
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-border-color-disabled)'
  }}`,
  error: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-3)'
  }}`,
  warning: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-3)'
  }}`
};

const textFieldStartContentStyles = {
  base: cssProps`${{
    marginInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-icon-to-edge-padding)',
    fontSize: ojButtonSmIconSize
  }}`,
  labelInside: cssProps`${{
    fontSize: ojButtonIconSize
  }}`,
  disabled: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-disabled)'
  }}`
};

const textFieldMiddleStyles = {
  base: cssProps`${{
    position: 'relative',
    // TODO: Use Flex component instead of CSS
    display: 'inline-flex',
    flex: 1,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box'
  }}`,
  textarea: cssProps`${{
    // The 1px is needed to make sure that the textarea element's scrollbar doesn't overlap the focus drop shadow
    paddingInlineEnd: '1px',
    // flexDirection 'column' is used to layout out the remaining counter below the textarea.
    flexDirection: 'column'
  }}`
};

const textFieldEndContentStyles = {
  base: cssProps`${{
    paddingInlineEnd: `calc((var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height) - ${ojButtonSmHeight} - 2px) / 2)`
  }}`,
  labelInside: cssProps`${{
    paddingInlineEnd: `calc((var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-height) - ${ojButtonHeight} - 2px) / 2)`
  }}`
};

// Renders the oj-text-field-container dom which includes the
// start, middle (where the inside label and inputElem goes),
// and end pieces of the form component.
// This does not include user assistance because that is rendered outside the
// oj-text-field-container; that is rendered in the TextField component.
export const TextFieldContent = ({
  contentVariant = 'input',
  insideLabel,
  mainContent,
  startContent,
  statusVariant = 'none',
  styleVariant = 'default',
  endContent,
  resize,
  rootRef
}: Props) => {
  const { isDisabled, isFocused, isLoading } = useFormFieldContext();

  const rootStyles = classNames([
    textFieldContainerStyles.base,
    styleVariant === 'embedded'
      ? classNames([
          textFieldContainerStyles.embeddedStyles,
          statusVariant !== 'none' && textFieldContainerStyles.showBorder
        ])
      : classNames([
          textFieldContainerStyles.defaultStyles,
          textFieldContainerStyles.showBorder,
          insideLabel !== undefined && textFieldContainerStyles.labelInside,
          isFocused && textFieldContainerStyles.focused,
          contentVariant === 'textarea' && textFieldContainerStyles.textarea
        ]),
    resize && textFieldContainerStyles.resize[resize],
    isDisabled
      ? textFieldContainerStyles.disabled
      : classNames([
          statusVariant === 'error' && textFieldContainerStyles.error,
          statusVariant === 'warning' && textFieldContainerStyles.warning,
          // TODO: don't use class names: JET-50572
          statusVariant !== 'none' && `oj-c-text-field-${statusVariant}`
        ]),
    isLoading && loadingStyles
  ]);
  const startContentStyles = classNames([
    textFieldStartContentStyles.base,
    insideLabel != undefined && textFieldStartContentStyles.labelInside,
    isDisabled && textFieldStartContentStyles.disabled
  ]);
  const middleStyles = classNames([
    textFieldMiddleStyles.base,
    contentVariant === 'textarea' && textFieldMiddleStyles.textarea
    // getMiddleContentVariantStyles(variant)
  ]);
  const endContentStyles = classNames([
    textFieldEndContentStyles.base,
    insideLabel != undefined && textFieldEndContentStyles.labelInside
  ]);

  return (
    <div role="presentation" class={rootStyles} ref={rootRef}>
      {startContent && (
        <span class={startContentStyles}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {startContent}
          </Flex>
        </span>
      )}
      <div class={middleStyles}>
        {insideLabel}
        {mainContent}
      </div>
      {endContent && (
        <span class={endContentStyles}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {endContent}
          </Flex>
        </span>
      )}
    </div>
  );
};
